import React from 'react';
import { centsToDollars } from '../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../rootReducer';
import { TTCheck } from '../../../interfaces/check';
import { Tooltip } from 'antd';
import { TTOrder, TTOrderCreate, TTOrderV2 } from '../../../interfaces/order';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { TTOrderItemCreate } from '../../../interfaces/order_item';

type Props = {
  from_order?: boolean;
  tip_callback?: () => void;
};

const CheckInfoCard: React.FC<Props> = ({
  from_order = false,
  tip_callback,
}) => {
  const check = useSelector<RootState, TTCheck>(state => {
    return from_order ? state.order.guest_check : state.cart.check;
  });

  const cart_items = useSelector<RootState, TTOrderItemCreate[]>(
    state => state.cart.cart_items,
  );

  const order = useSelector<RootState, TTOrderV2 | null>(
    state => state.order.order,
  );

  if (
    cart_items?.filter(c => c.product_type !== 'subscription')?.length === 0 &&
    check?.total === 0
  ) {
    return <></>;
  }

  if (
    from_order &&
    order?.order_tabs
      ?.map(t => t.order_items)
      ?.reduce((a, b) => a.concat(b), [])
      ?.filter(i => i.product_type !== 'subscription')?.length === 0
  ) {
    return <></>;
  }

  return (
    <div className="py-2 pr-4 pl-4 sm:h-1/5 max-h-1/5  rounded bg-surface">
      <div className="flex flex-col bg-primary/10 p-4 items-center rounded-lg  justify-between">
        <div className="flex flex-row mr-1 w-full mb-2 justify-between">
          <div className="text-on-surface  text-sm  text-left justify-left">
            Item Total
          </div>
          <div className="text-on-surface text-sm text-right justify-right">
            {centsToDollars(check.sub_total)}
          </div>
        </div>
        {check.restaurant_discount != 0 && (
          <div className="flex flex-row mr-1 w-full mb-2  justify-between">
            <div className="text-on-surface  text-sm  text-left justify-left">
              Discount
            </div>
            <div className="text-on-surface text-sm  text-right justify-right">
              -{centsToDollars(check.restaurant_discount)}
            </div>
          </div>
        )}
        <div className="flex flex-row mr-1 w-full mb-2 justify-between items-center">
          <div className="flex flex-row justify-start items-center">
            <div className="text-on-surface  text-sm  text-left justify-left">
              Tip
            </div>
            {/* {(from_order || !order?.is_single_session) && (
              <div
                className="ml-2  px-1 text-xs py-0.5 rounded-full bg-tertiary text-on-tertiary"
                onClick={tip_callback}
              >
                Change
              </div>
            )} */}
          </div>

          <div className="text-on-surface text-sm text-right justify-right">
            {centsToDollars(check.tip)}
          </div>
        </div>
        {check.restaurant_service_charges
          ?.filter(c => c.taxable && c?.amount !== 0)
          ?.map(c => {
            return (
              <div
                className="flex flex-row mr-1 w-full mb-2 justify-between"
                key={c.id?.toString()}
              >
                <div className="text-on-surface text-sm  text-left justify-left items-center">
                  {c.description !== null && (
                    <Tooltip
                      title={c.description}
                      trigger={['click', 'hover', 'focus']}
                      className={'items-center'}
                    >
                      <div className="flex flex-row justify-start items-center space-x-1">
                        <div>{c.name ?? 'Service Charge'}</div>
                        <InformationCircleIcon
                          color={'on-surface'}
                          className={'w-4 h-4 text-on-surface '}
                        />
                      </div>
                    </Tooltip>
                  )}
                  {c.description === null && (
                    <div>{c.name ?? 'Service Charge'}</div>
                  )}
                </div>
                <div className="text-on-surface  text-sm text-right justify-right">
                  {centsToDollars(c.amount)}
                </div>
              </div>
            );
          })}
        <div className="flex flex-row mr-1 w-full mb-2 justify-between">
          <div className="text-on-surface text-sm  text-left justify-left">
            {'Taxes'}
          </div>
          <div className="text-on-surface  text-sm  text-right justify-right">
            {centsToDollars(check.tax)}
          </div>
        </div>
        <div className="flex flex-row mr-1 w-full mb-2 justify-between">
          <div className="text-on-surface text-sm  text-left justify-left">
            {'Platform Service Fee'}
          </div>
          <div className="text-on-surface  text-sm  text-right justify-right">
            {centsToDollars(check.service_fee ?? 0)}
          </div>
        </div>
        {check.restaurant_service_charges
          ?.filter(c => !c.taxable && c?.amount !== 0)
          ?.map(c => {
            return (
              <div
                className="flex flex-row mr-1  mb-2 w-full justify-between"
                key={c.id?.toString()}
              >
                <div className="text-on-surface text-sm  text-left justify-left items-center">
                  {c.description !== null && (
                    <Tooltip
                      title={c.description}
                      trigger={['click', 'hover', 'focus']}
                      className={'items-center'}
                    >
                      <div className="flex flex-row justify-start items-center space-x-1">
                        <div>{c.name ?? 'Service Charge'}</div>
                        <InformationCircleIcon
                          color={'on-surface'}
                          className={'w-4 h-4 text-on-surface '}
                        />
                      </div>
                    </Tooltip>
                  )}
                  {c.description === null && (
                    <div>{c.name ?? 'Service Charge'}</div>
                  )}
                </div>
                <div className="text-on-surface  text-sm  text-right justify-right">
                  {centsToDollars(c.amount)}
                </div>
              </div>
            );
          })}
        <div className="flex flex-row mr-1 w-full justify-between">
          <div className="text-on-surface font-bold text-sm  text-left justify-left">
            Total
          </div>
          <div className="text-on-surface font-bold text-sm  text-right justify-right">
            {centsToDollars(check.total + check.service_fee)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInfoCard;
